import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useCreateTrunkType from './queries/useCreateTrunkType';

import FormBase from '@/components/forms/new/FormBase';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrail,
    addHeader as header,
    initialState,
    trunkTypesPath,
} from './constants';
import { trunkTypeSchema } from './schema';
import TrunkTypeNameField from './formFields/TrunkTypeNameField';
import HeaderValueField from './formFields/HeaderValueField';
import MinChannelsField from './formFields/MinChannelsField';
import IncSizeField from './formFields/IncSizeField';
import TeamsEnabledField from './formFields/TeamsEnabledField';
import SipEnabledField from './formFields/SipEnabledField';
import SharedField from './formFields/SharedField';

const TrunkTypesAdd = () => {
    const navigate = useNavigate();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);

    const handleSuccess = () => {
        toast.success('Trunk type created successfully!');
        navigate(trunkTypesPath);
    };

    const handleError = (err) => {
        console.error('Failed to create trunk type:', err);
        setError(
            err?.response?.data?.message ||
                'Failed to create trunk type. Please try again.',
        );
    };

    const { mutate: createTrunkType, isPending } = useCreateTrunkType(
        handleSuccess,
        handleError,
    );

    const finalFormSubmit = async (values) => {
        setError(null);

        let validated;
        try {
            validated = await trunkTypeSchema.validate(values);
        } catch (validationError) {
            setError(validationError.message);
            return;
        }

        createTrunkType({
            name: validated.name,
            headerValue: validated.headerValue,
            minChannels: Number(validated.minChannels),
            incSize: Number(validated.incSize),
            teamsEnabled: !!values.teamsEnabled,
            sipEnabled: !!values.sipEnabled,
            shared: !!values.shared,
        });
    };

    const formFields = (errors, touched, values, setFieldValue) =>
        [
            <TrunkTypeNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <HeaderValueField
                key="headerValue"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <MinChannelsField
                key="minChannels"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <IncSizeField
                key="incSize"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <TeamsEnabledField
                key="teamsEnabled"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            <SipEnabledField
                key="sipEnabled"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            <SharedField
                key="shared"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    return (
        <FormBase
            isSubmitDisabled={isPending}
            isLoading={isPending}
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={initialState}
            finalFormSubmit={finalFormSubmit}
            backLink={[trunkTypesPath, { state: { index: 0 } }]}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default TrunkTypesAdd;
